import NoVisualization from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Components/NoVisualization/NoVisualization';
import VisualizationContainer from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Containers/Visualization';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import clsx from 'clsx';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import LayerFadeImage from '../Components/Background/LayerFadeImage';
import CallToActionBox from '../Components/Buttons/CallToActionBox';
import StartButton from '../Components/Buttons/StartButton';
import ComponentSelector from '../Components/Configurator/ComponentSelector';
import VisualizationTools from '../Components/Configurator/VisualizationTools';
import { AccessDeniedDialog } from '../Components/Forms/AccessDeniedDialog';
import { SaveConfigurationDialog } from '../Components/Forms/SaveConfigurationDialog';
import DebugBox from '../Components/Debug/DebugBox';
import ProductParameters from '../Components/Forms/ProductParameters';
import SystemParameters from '../Components/Forms/SystemParameters';
import Header from '../Components/Header/Header';
import LoadConfiguration from '../Containers/LoadConfiguration';
import { darkBlue } from '../Constants/Colors';
import {
  getBackgroundImage,
  isConfigurationLoaded,
  isConfiguratorStep,
  isProductParametersStep,
  isStartScreen,
  isSystemParametersStep,
} from '../Reducers/App/Selectors';
import { Hotmelt } from '../Resources/images';
import { setLanguage } from '../utils/language';

const styles = (theme) => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: darkBlue,
  },
  fallbackMode: {},
  header: {
    background: darkBlue,
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    position: 'relative',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
  },
  main: {
    position: 'relative',
  },
  visualization: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  designer: {},
  colMid: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  containerPortrait: {
    flexDirection: 'column',
    '& $colMid': {
      marginLeft: 0,
      marginRight: 0,
      marginBottom: theme.spacing(3),
      width: '100%',
      flex: 1,
      order: 1,
    },
  },
  backButton: {
    display: 'flex',
    gap: '12px',
    paddingLeft: 0,
    textTransform: 'uppercase',
  },
  arrowLeft: {
    transform: 'rotate(180deg)',
  },
  addToCart: {
    marginTop: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  subtitle: {
    textTransform: 'none',
  },
  callToActionBox: {
    padding: '2rem 0',
    margin: `1rem 2rem 0 2rem`,
    display: 'flex',
    justifyContent: 'flex-end',
    '&.withBorder': {
      borderTop: '1px solid red',
      background: darkBlue,
    },
    '&:not(.withBorder)': {
      position: 'absolute',
      bottom: 0,
      right: 0,
    },
  },
});

const Layout = ({
  classes,
  renderLoader,
  renderLicenseNotice,
  renderSendEmail,
  renderPdfDownload,
  renderError,
  renderAlertMessages,
  renderConfirm,
}) => {
  const startScreen = useSelector(isStartScreen);
  const showConfigurator = useSelector(isConfiguratorStep);
  const backgroundImage = useSelector(getBackgroundImage);
  const configurationLoaded = useSelector(isConfigurationLoaded);
  const showSystemParameters = useSelector(isSystemParametersStep);
  const showProductParameters = useSelector(isProductParametersStep);

  useEffect(() => {
    const language = Cookies.get('language');
    if (language) {
      setLanguage(language);
    }
  }, []);

  const renderContent = () => {
    return (
      <>
        {!showConfigurator && <StartButton />}
        {showSystemParameters && <SystemParameters />}
        {showProductParameters && <ProductParameters />}
        {showConfigurator && <ComponentSelector />}
        {showConfigurator && (
          <div className={clsx(classes.visualization)}>
            <VisualizationContainer BrokenComponent={NoVisualization} />
            <VisualizationTools />
          </div>
        )}
        <div className={classes.main}>
          <AccessDeniedDialog />
          {renderError()}
          {renderAlertMessages()}
          {renderConfirm()}
          <SaveConfigurationDialog />
          {renderSendEmail()}
          {renderPdfDownload()}
          <LoadConfiguration />
        </div>
      </>
    );
  };

  return (
    <div className={classes.root}>
      {configurationLoaded && renderLicenseNotice()}
      <div className={classes.header}>
        <Header />
      </div>
      <div className={classes.container}>
        <LayerFadeImage src={backgroundImage || Hotmelt} crossfade />
        <div className={classes.colMid}>{renderContent()}</div>
      </div>
      {!startScreen && renderLoader()}
      <CallToActionBox
        className={clsx(
          classes.callToActionBox,
          (showSystemParameters || showProductParameters) && 'withBorder'
        )}
      />
      <DebugBox />
    </div>
  );
};

Layout.propTypes = {
  classes: PropTypes.object,
  width: PropTypes.string,
  renderHeader: PropTypes.func,
  renderMain: PropTypes.func,
  renderSidebar: PropTypes.func,
  renderVisualization: PropTypes.func,
  renderLoader: PropTypes.func,
  renderLicenseNotice: PropTypes.func,
  fallbackMode: PropTypes.bool,
  isPortraitMode: PropTypes.bool,
  imageOnly: PropTypes.bool,
  renderSaveConfiguration: PropTypes.func,
  renderSendEmail: PropTypes.func,
  renderPdfDownload: PropTypes.func,
  renderLoadConfiguration: PropTypes.func,
  renderError: PropTypes.func,
  renderAlertMessages: PropTypes.func,
  renderConfirm: PropTypes.func,
};

export default withStyles(styles, { name: 'Layout' })(withWidth({ initialWidth: 'xl' })(Layout));
